import { AxiosResponse, CancelTokenSource } from 'axios';
import { CountryCodeType } from '../components/LanguageSelector/Flag';
import { BookingSourceType } from '../enums/BookingSourceType';
import { PaymentType } from '../enums/PaymentType';
import { BookingProcessStoreNS } from '../stores/useBookingProcessStore';
import API from './index';
import { CompleteBookingResponseModelType } from '../payments/BaseCompleteResponseModel';
import { CurrencyISO4217 } from '../types/CurrencyISO4217';
import { BookingStatusType } from '../enums/BookingStatusType';

export type BookingMenuSelection = {
    id: number;
    name: string;
    paymentType: PaymentType;
    paymentAmount: number | null;
    count: number;
};

type Card = {
    pan: string;
    expiryMonth: string;
    expiryYear: string;
    cvc: number;
};

export default class BookingAPI extends API {
    reserve(
        dateTime: string,
        guests: number,
        restaurantId: number,
        eventId: number | undefined,
        source: BookingSourceType,
        menus: Array<{ menuId: number; count: number }>,
        isWaitingListWhenFullyBooked: boolean,
        cancelToken: CancelTokenSource,
        token: string,
        captchaToken: string,
    ): Promise<
        AxiosResponse<{ success: boolean; guid?: string; expires?: string }>
    > {
        return this._post(
            '/web/booking/reserve',
            {
                date: dateTime,
                guests,
                restaurantId,
                eventId,
                source,
                menus,
                isWaitingListWhenFullyBooked,
                captchaToken,
            },
            {
                cancelToken: cancelToken.token,
                headers: {
                    Authorization: 'Bearer ' + token,
                },
            },
        );
    }

    complete(
        guid: string,
        bookingName: string,
        bookingPhone: string,
        phoneNumberRegion: string | null,
        bookingEmail: string,
        bookingSpecialRequest: string,
        cardNumber: string,
        cardExpiryMonth: string,
        cardExpiryYear: string,
        cardCvc: number | null,
        allowSMSMarketing: boolean,
        allowRestaurantEmails: boolean,
        referrerCode: string | null,
        isWaitingList: boolean,
        bookingDietaryRestrictions: string,
        hasAllergies: boolean,
        cancelToken: CancelTokenSource,
        verificationCode: string | null = null,
    ): Promise<AxiosResponse<CompleteBookingResponseModelType>> {
        const payload: Record<string, unknown> = {
            guid,
            bookingName,
            bookingPhone,
            phoneNumberRegion,
            bookingEmail,
            bookingSpecialRequest,
            cardNumber,
            cardExpiryMonth,
            cardExpiryYear,
            cardCvc,
            allowSMSMarketing,
            allowRestaurantEmails,
            referrerCode,
            isWaitingList,
            bookingDietaryRestrictions,
            hasAllergies,
        };

        if (verificationCode) {
            payload.verificationCode = verificationCode;
        }

        return this._put('/web/booking/complete/v2', payload, {
            cancelToken: cancelToken.token,
        });
    }

    resendVerification(
        guid: string,
        cancelToken: CancelTokenSource,
    ): Promise<AxiosResponse<string>> {
        return this._post(
            '/web/booking/verification/resend',
            { guid },
            {
                cancelToken: cancelToken.token,
            },
        );
    }

    getBookingInfoForComplete(
        guid: string,
        isWaitingListBooking: boolean,
        cancelToken: CancelTokenSource,
    ): Promise<
        AxiosResponse<{
            start: string;
            guests: number;
            restaurantId: number;
            restaurantName: string;
            restaurantCountry: CountryCodeType;
            expires: string;
            menus: BookingMenuSelection[];
            paymentRequirements: {
                totalNoShow: number;
                totalPrepaid: number;
            };
            gaTrackingId: string | null;
            fbPixelId: string | null;
            policyText: string | null;
            noShowText: string | null;
            requirePolicyAccept: boolean;
            requireEmailMarketingConfirmation: boolean;
            requireAllergenInfo: boolean;
            isExternalPaymentForm: boolean;
            currency: CurrencyISO4217;
            locale: string;
        }>
    > {
        return this._get(
            `/web/booking/v3/bookinginfoforcomplete/${guid}/${isWaitingListBooking}`,
            {
                cancelToken: cancelToken.token,
            },
        );
    }

    getBookingInfoForConfirmation(
        guid: string,
        cancelToken: CancelTokenSource,
    ): Promise<
        AxiosResponse<{
            bookingId: number;
            start: string;
            end: string;
            guests: number;
            status: BookingStatusType;
            redirectUrl: string | null;
            restaurantId: number;
            restaurantSlug: string;
            restaurantName: string;
            restaurantAddress: string;
            restaurantWebsite: string;
            restaurantNote: string;
            restaurantLon: number;
            restaurantLat: number;
            logoUrl: string;
            gaTrackingId: string | null;
            fbPixelId: string | null;
            adImage: { imageUrl: string; href: string } | null;
        }>
    > {
        return this._get(`/web/booking/bookinginfoforconfirmation/${guid}`, {
            cancelToken: cancelToken.token,
        });
    }

    getPaymentLinkData(
        guid: string,
        cancelToken: CancelTokenSource,
    ): Promise<
        AxiosResponse<{
            success: boolean;
            paymentType: PaymentType;
            fee: number | null;
            logoUrl: string | null;
            message: string;
            isExternalPaymentForm: boolean;
            currency: string;
            locale: string;
        }>
    > {
        return this._get(`/web/booking/paymentlink/${guid}`, {
            cancelToken: cancelToken.token,
        });
    }

    paymentLinkCardConfirm(
        paymentLinkData: { guid: string; card: Card | null },
        cancelToken: CancelTokenSource,
    ): Promise<AxiosResponse<CompleteBookingResponseModelType>> {
        const { guid, card } = paymentLinkData;

        return this._put(`/web/booking/paymentlink/confirm`, {
            guid,
            card: card
                ? {
                      number: card.pan,
                      expiryMonth: card.expiryMonth,
                      expiryYear: card.expiryYear,
                      cvc: card.cvc,
                  }
                : null,
            cancelToken: cancelToken.token,
        });
    }

    cancelBookingByGuid(
        guid: string,
        cancelToken: CancelTokenSource,
    ): Promise<
        AxiosResponse<{
            success: boolean;
            message: string;
        }>
    > {
        return this._put(`/web/booking/cancel/${guid}`, {
            cancelToken: cancelToken.token,
        });
    }

    isCancelAllowed(
        guid: string,
        cancelToken: CancelTokenSource,
    ): Promise<
        AxiosResponse<{
            success: boolean;
            message: string;
            isWaitingList: boolean;
        }>
    > {
        return this._get(`/web/booking/iscancelallowed/${guid}`, {
            cancelToken: cancelToken.token,
        });
    }

    getBookingProcessOrder(
        restaurantId: number,
        eventId: number | null,
        isWaitingList: boolean,
        dateTime: string,
        cancelToken: CancelTokenSource,
    ): Promise<
        AxiosResponse<{ bookingProcessSteps: BookingProcessStoreNS.Type[] }>
    > {
        return this._get(
            `/web/booking/v4/processsteps/${restaurantId}/${eventId}/${isWaitingList}/${dateTime}`,
            {
                cancelToken: cancelToken.token,
            },
        );
    }
}
